import { Button, Input } from '@components/common';
import { Pencil, Plus } from 'lucide-react';
import React from 'react';

export interface InventorySettingsTableRow {
  id: string;
  title: string;
  subtitle?: string | null;
  meta?: string | null;
  status: boolean;
}

interface InventorySettingsTableProps {
  title: string;
  description: string;
  searchLabel: string;
  searchValue: string;
  createLabel: string;
  rows: InventorySettingsTableRow[];
  emptyMessage: string;
  onSearchChange: (value: string) => void;
  onCreate: () => void;
  onEdit: (id: string) => void;
  onToggleStatus: (id: string) => void;
}

export function InventorySettingsTable({
  title,
  description,
  searchLabel,
  searchValue,
  createLabel,
  rows,
  emptyMessage,
  onSearchChange,
  onCreate,
  onEdit,
  onToggleStatus,
}: InventorySettingsTableProps) {
  return (
    <section className="rounded-[24px] border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold text-slate-900">{title}</p>
          <p className="mt-1 text-sm text-slate-500">{description}</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-[260px_auto]">
          <Input
            label={searchLabel}
            value={searchValue}
            onChange={onSearchChange}
            placeholder="Search..."
          />
          <div className="flex items-end">
            <Button className="w-full sm:w-auto" onClick={onCreate}>
              <Plus size={16} className="mr-1" />
              {createLabel}
            </Button>
          </div>
        </div>
      </div>

      {rows.length ? (
        <div className="mt-4 overflow-hidden rounded-2xl border border-gray-100">
          <div className="grid grid-cols-[minmax(0,1.4fr)_160px_170px] gap-3 border-b border-gray-100 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
            <span>Record</span>
            <span>Status</span>
            <span className="text-right">Actions</span>
          </div>
          <div className="max-h-[340px] overflow-y-auto">
            {rows.map((row) => (
              <div
                key={row.id}
                className="grid grid-cols-[minmax(0,1.4fr)_160px_170px] gap-3 border-b border-gray-100 px-4 py-3 last:border-b-0"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-900">
                    {row.title}
                  </p>
                  {row.subtitle ? (
                    <p className="truncate text-xs text-slate-500">
                      {row.subtitle}
                    </p>
                  ) : null}
                  {row.meta ? (
                    <p className="truncate text-[11px] text-slate-400">
                      {row.meta}
                    </p>
                  ) : null}
                </div>
                <div className="flex items-center">
                  <span
                    className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                      row.status
                        ? 'bg-pos-primary/10 text-pos-primary'
                        : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {row.status ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex items-center justify-end gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onEdit(row.id)}
                  >
                    <Pencil size={14} className="mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="text"
                    onClick={() => onToggleStatus(row.id)}
                  >
                    {row.status ? 'Deactivate' : 'Activate'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-4 rounded-2xl border border-dashed border-gray-200 bg-slate-50 px-4 py-8 text-sm text-slate-500">
          {emptyMessage}
        </div>
      )}
    </section>
  );
}
